import { CollectionHelper, CompareHelper, ConvertHelper, DataUrl, GUID } from '@barthauer/abc/core';
import {
  CacheStorageAdapter,
  IPersistable,
  ObjectSpaceService,
  ObjectTypeInfoViewModel,
  PersistenceService,
  PersistMode,
  ReadListDataSourceResult,
} from '@barthauer/abc/core/data';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IFilterable } from '../models/componentDataSource.model';
import { MtDocument, MtObject, MtResult, MtResultType, MtTask, MtWork, Order } from '../models/mtData.model';
import { DataService } from '../services/data.service';
import { MTHelper, ViewModelHelper } from '../shared/helpers';
import { WorkState, WorkTypeDurationMode } from '../works/works.model';
import { WorkConstants } from './../works/works.model';
import { MobileDocumentViewModel } from './mobileData.common.viewModel';

export interface IDrawable {
  drawableObjectId: string;
  drawableObjectType: number | string;
}
export interface ISelfDescribable {
  getValues(): string[];
}

export interface IMobileWork extends ISelfDescribable {
  sortOrder: number;
  designation: string;
  objectDesignation: string;
  objectObjectType: string;
  street: string;
  note: string;
}

export interface IMobileResult {
  selected: boolean;
  designation: string;
  id: string;
}

export class ListDataSourceItem {
  public value: string;
  public description: string;

  constructor(dataItem: object) {
    this.value = dataItem['Value'];
    this.description = dataItem['Description'];
  }
}

export class ListDataSource {
  public dataItems: ListDataSourceItem[];
  constructor(result: ReadListDataSourceResult) {
    this.dataItems = new Array<ListDataSourceItem>();
    if (result.Data) {
      for (const dataItem of result.Data) {
        this.dataItems.push(new ListDataSourceItem(dataItem));
      }
    }
  }
}

export class MobileObjectViewModel implements IDrawable {
  get objectId(): string {
    return this.mtObject?.Id;
  }
  get objectType(): number {
    return this.mtObject?.ObjectType;
  }
  get drawableObjectId(): string {
    return this.mtObject?.GeObjectId;
  }
  get drawableObjectType(): number {
    return this.mtObject?.GeObjectType;
  }
  get type(): string {
    return this.mtObject?.Type; // is ref: expression (if filled at all)
  }
  get designation(): string {
    return this.mtObject?.Designation;
  }
  get street(): string {
    return this.mtObject?.Street;
  }
  get note(): string {
    return this.mtObject?.Note;
  }

  private wasMainTypeCalculated = false;

  private _mainTypeObjectType: ObjectTypeInfoViewModel;
  public get mainTypeObjectType(): ObjectTypeInfoViewModel {
    return this._mainTypeObjectType;
  }

  public get mainTypeDisplayTextSingular(): string {
    return (!this.mainTypeObjectType || this.mainTypeObjectType?.isMatch('MtObject')) && this.type
      ? this.type
      : (this.mainTypeObjectType?.displayText ??
          this.mainTypeObjectType?.name ??
          `Object with ObjectType ${this.drawableObjectType}`);
  }

  public get mainTypeDisplayTextPlural(): string {
    return (!this.mainTypeObjectType || this.mainTypeObjectType?.isMatch('MtObject')) && this.type
      ? `List of ${this.type} objects`
      : (this.mainTypeObjectType?.displayTextPlural ??
          (this.mainTypeObjectType ? `List of ${this.mainTypeObjectType.name} objects` : null) ??
          `List of objects with ObjectType ${this.drawableObjectType}`);
  }

  public async calcMainType(objectSpaceService$: ObjectSpaceService, force: boolean = false): Promise<void> {
    if (!this.wasMainTypeCalculated || force) {
      const getMainObjectTypeObservable$: Observable<void> = (
        this.drawableObjectType && this.drawableObjectType > 0
          ? objectSpaceService$.getObjectType(this.drawableObjectType)
          : objectSpaceService$.getObjectType('MtObject')
      ).pipe(
        map((drawableOt) => {
          if (drawableOt) {
            if (drawableOt.isMatch('MtObject')) {
              this._mainTypeObjectType = drawableOt;
            } else {
              this._mainTypeObjectType = drawableOt.mainType ?? drawableOt;
            }
          } else {
            this._mainTypeObjectType = null;
          }

          this.wasMainTypeCalculated = true;
        }),
      );

      // Ensure completion/unsubscribe directly after first emit (not after complete())
      return getMainObjectTypeObservable$.pipe(take(1)).toPromise(); // mimic firstValueFrom() behavior in RxJS <7
      //With RxJS >=7 toPromise() is deprecated. Instead: return firstValueFrom(getMainObjectTypeObservable$);
    }
    // No need for else branch because auf async/await handling -> Noice!
  }

  constructor(private mtObject: MtObject) {}
}

export class MobileWorkViewModel implements IMobileWork, IPersistable, IDrawable, IFilterable {
  isVirtual: boolean;

  private _tasks: MobileTaskViewModel[];
  private _object: MobileObjectViewModel;

  private persistenceService: PersistenceService;
  private mtWork: MtWork;
  private resultTypesGetter: (positionTypeId: string) => MtResultType[];
  private isResultTypeSelectedByDefault: (positionTypeId: string, resultTypeId: string) => boolean;
  private isResultTypeSelectedByAutoComplete: (positionTypeId: string, resultTypeId: string) => boolean;
  private getResultTypeSortOrder: (positionTypeId: string, resultTypeId: string) => number;
  private getResultTypeDesignation: (positionTypeId: string, resultTypeId: string) => string;
  private getResultTypeFollowingTaskTypeId: (positionTypeId: string, resultTypeId: string) => string;
  private getResultTypeInvisible: (positionTypeId: string, resultTypeId: string) => boolean;
  public managedWorks: MobileWorkViewModel[];
  private virtualDesignation: string;
  private virtualObjectDesignation: string;
  private virtualObjectType: string;
  private virtualRelevantForEdges: boolean;
  private virtualRelevantForStartNode: boolean;
  private virtualRelevantForEndNode: boolean;
  private virtualRelevantForMidNodes: boolean;
  private virtualWorkTypeDurationMode: string;
  private virtualDuration: number;
  private virtualGeneratedFromEntity: DataEntity;

  get id(): string {
    return this.mtWork?.Id;
  }
  get objectType(): string {
    return WorkConstants.ObjectTypeName_MtWork;
  }
  get persistenceCategory(): string {
    return WorkConstants.PersistenceCategory_WorkDetail;
  }
  get sortOrder(): number {
    return this.mtWork?.SortOrder;
  }
  get designation(): string {
    return this.isVirtual ? this.virtualDesignation : this.mtWork.Designation;
  }
  get executionDate(): string {
    return this.mtWork?.ExecutionDate;
  }
  set executionDate(value: string) {
    this.mtWork.ExecutionDate = value;
  }
  get state(): number {
    return this.mtWork.State;
  }
  set state(value: number) {
    this.mtWork.State = value;
  }
  get stateIconName(): string {
    if (!this.isVirtual) {
      switch (this.state) {
        case WorkState.Teilerledigt:
          return 'StatePartial-Image';
        case WorkState.Erledigt:
          return 'StateDone-Image';
        case WorkState.Geplant:
        case WorkState.Anstehend:
        case WorkState.Beauftragt:
          return 'StateOpen-Image';
      }
    } else {
      return 'WorkAggregatePath-Image';
    }
    return null;
  }

  get headerTypeDisplayText(): string {
    if (this.isVirtual) {
      return this.getAggregatedModeCategoryText() + ':';
    } else if (this.objectObjectType) {
      return this.objectObjectType + ':';
    } else {
      return this.defaultHeaderTypeDisplayText;
    }
  }
  get stateDisplayText(): string {
    return this.mtWork.StateDisplayText;
  }

  get object(): MobileObjectViewModel {
    return this._object;
  }
  get objectDesignation(): string {
    return this.isVirtual ? this.virtualObjectDesignation : this.object.designation;
  }
  get objectObjectType(): string {
    return this.isVirtual ? this.virtualObjectType : this.object.type;
  }
  get street(): string {
    return this.object?.street;
  }
  get note(): string {
    return this.object?.note;
  }
  get drawableObjectId(): string {
    return this.object.drawableObjectId;
  }
  get drawableObjectType(): number {
    return this.object.drawableObjectType;
  }
  get filterableObjectId(): string {
    return this.object.drawableObjectId;
  }
  get filterableObjectType(): number {
    return this.object.drawableObjectType;
  }
  get filterableDisplayText(): string {
    return this.objectObjectType + ' ' + this.objectDesignation;
  }
  get allowDeletion(): boolean {
    return this.mtWork?.AllowDeletion;
  }
  get workTypeDurationMode(): string {
    return this.isVirtual ? this.virtualWorkTypeDurationMode : this.mtWork?.WorkTypeDurationMode;
  }
  get workTypeDurationModeValue(): WorkTypeDurationMode {
    return Number(this.workTypeDurationMode ?? 0) as WorkTypeDurationMode;
  }
  get duration(): number {
    return this.isVirtual ? this.virtualDuration : this.mtWork?.Duration;
  }
  set duration(value: number) {
    if (this.isVirtual) {
      for (const managedWork of this.managedWorks) {
        managedWork.duration = value;
      }
      this.virtualDuration = value;
    } else if (this.mtWork.Duration !== value) {
      this.persistenceService.addObjectValue(this, 'Duration', value, this.mtWork.Duration);
      this.mtWork.Duration = value;
    }
  }

  get tasks(): MobileTaskViewModel[] {
    if (!this._tasks) {
      this._tasks = [];
      if (this.isVirtual) {
        let managedTasks: MobileTaskViewModel[] = this.managedWorks.reduce((pn, u) => [...pn, ...u.tasks], []);
        managedTasks = managedTasks.filter(
          (_) =>
            _.relevantForEdges === this.virtualRelevantForEdges &&
            _.relevantForStartNode === this.virtualRelevantForStartNode &&
            _.relevantForEndNode === this.virtualRelevantForEndNode &&
            _.relevantForMidNodes === this.virtualRelevantForMidNodes,
        );

        if (managedTasks.length) {
          const groupedTasks: Map<string, MobileTaskViewModel[]> = ViewModelHelper.groupArray<MobileTaskViewModel>(
            managedTasks,
            'designation',
          );

          groupedTasks.forEach((v, k) => {
            const virtualTask = new MobileTaskViewModel();
            const firstRealTask: MobileTaskViewModel = v[0];
            const uniqueDescription = v.every(
              (t) => t.description?.toLowerCase() === firstRealTask.description?.toLowerCase(),
            )
              ? firstRealTask.description
              : null;
            const uniqueAllowMultiSelectResults = v.every(
              (t) => t.allowMultiSelectResults === firstRealTask.allowMultiSelectResults,
            )
              ? firstRealTask.allowMultiSelectResults
              : null;
            const uniqueAllowSelectResultsFromSingleCategory = v.every(
              (t) => t.allowSelectResultsFromSingleCategory === firstRealTask.allowSelectResultsFromSingleCategory,
            )
              ? firstRealTask.allowSelectResultsFromSingleCategory
              : null;
            const uniqueResultsRequired = v.every((t) => t.resultsRequired === firstRealTask.resultsRequired)
              ? firstRealTask.resultsRequired
              : null;
            const uniqueFinished = v.every((t) => t.finished === firstRealTask.finished)
              ? firstRealTask.finished
              : null;
            virtualTask.loadVirtual(
              firstRealTask.designation,
              uniqueDescription,
              firstRealTask.sortOrder,
              uniqueAllowMultiSelectResults,
              uniqueAllowSelectResultsFromSingleCategory,
              uniqueResultsRequired,
              this.virtualRelevantForEdges,
              this.virtualRelevantForStartNode,
              this.virtualRelevantForEndNode,
              this.virtualRelevantForMidNodes,
              uniqueFinished,
              v,
            );
            this._tasks.push(virtualTask);
          });
        }
      } else {
        for (const task of this.mtWork.Tasks) {
          const t: MobileTaskViewModel = new MobileTaskViewModel();
          t.load(
            this.persistenceService,
            task,
            this,
            this.resultTypesGetter,
            this.isResultTypeSelectedByDefault,
            this.isResultTypeSelectedByAutoComplete,
            this.getResultTypeSortOrder,
            this.getResultTypeDesignation,
            this.getResultTypeFollowingTaskTypeId,
            this.getResultTypeInvisible,
          );
          this._tasks.push(t);
        }
      }
      this._tasks.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : b.sortOrder > a.sortOrder ? -1 : 0));
    }
    return this._tasks;
  }

  hasState(workState: WorkState) {
    if (!this.isVirtual) {
      return this.state === workState;
    }
    return true;
  }

  async load(
    persistenceService: PersistenceService,
    objectSpaceService: ObjectSpaceService,
    mtWork: MtWork,
    resultTypesGetter: (positionTypeId: string) => MtResultType[],
    isResultTypeSelectedByDefault: (positionTypeId: string, resultTypeId: string) => boolean,
    isResultTypeSelectedByAutoComplete: (positionTypeId: string, resultTypeId: string) => boolean,
    getResultTypeSortOrder: (positionTypeId: string, resultTypeId: string) => number,
    getResultTypeDesignation: (positionTypeId: string, resultTypeId: string) => string,
    getResultTypeFollowingTaskTypeId: (positionTypeId: string, resultTypeId: string) => string,
    getResultTypeInvisible: (positionTypeId: string, resultTypeId: string) => boolean,
  ): Promise<void> {
    this.persistenceService = persistenceService;
    this.mtWork = mtWork;
    this.resultTypesGetter = resultTypesGetter;
    this.isResultTypeSelectedByDefault = isResultTypeSelectedByDefault;
    this.isResultTypeSelectedByAutoComplete = isResultTypeSelectedByAutoComplete;
    this.getResultTypeSortOrder = getResultTypeSortOrder;
    this.getResultTypeDesignation = getResultTypeDesignation;
    this.getResultTypeFollowingTaskTypeId = getResultTypeFollowingTaskTypeId;
    this.getResultTypeInvisible = getResultTypeInvisible;
    this._object = new MobileObjectViewModel(mtWork.Object);
    await this._object.calcMainType(objectSpaceService);
  }

  loadVirtual(
    designation: string,
    objectDesignation: string,
    objectType: string,
    relevantForEdges: boolean,
    relevantForStartNode: boolean,
    relevantForEndNode: boolean,
    relevantForMidNodes: boolean,
    workTypeDurationMode: string,
    duration: number,
    generatedFromEntity: DataEntity,
    managedWorks: MobileWorkViewModel[],
  ): void {
    this.isVirtual = true;
    this.managedWorks = managedWorks;
    this.virtualDesignation = designation;
    this.virtualObjectDesignation = objectDesignation;
    this.virtualObjectType = objectType;
    this.virtualGeneratedFromEntity = generatedFromEntity;
    this.virtualRelevantForEdges = relevantForEdges;
    this.virtualRelevantForStartNode = relevantForStartNode;
    this.virtualRelevantForEndNode = relevantForEndNode;
    this.virtualRelevantForMidNodes = relevantForMidNodes;
    this.virtualWorkTypeDurationMode = workTypeDurationMode;
    this.virtualDuration = duration;
    if (managedWorks && generatedFromEntity) {
      for (const work of managedWorks) {
        work.virtualGeneratedFromEntity = generatedFromEntity;
        this.isResultTypeSelectedByDefault = work.isResultTypeSelectedByDefault;
        this.isResultTypeSelectedByAutoComplete = work.isResultTypeSelectedByAutoComplete;
        this.getResultTypeSortOrder = work.getResultTypeSortOrder;
        this.getResultTypeDesignation = work.getResultTypeDesignation;
        this.getResultTypeFollowingTaskTypeId = work.getResultTypeFollowingTaskTypeId;
        this.getResultTypeInvisible = work.getResultTypeInvisible;
      }
    }
  }

  public mergeWithWork(
    virtualWork: MobileWorkViewModel,
    edges: boolean,
    startNode: boolean,
    endNode: boolean,
    midNodes: boolean,
  ): void {
    for (const managedWork of virtualWork.managedWorks) {
      if (this.managedWorks.findIndex((_) => _ === managedWork) < 0) {
        this.managedWorks.push(managedWork);
      }
    }
    if (!this._tasks) {
      this._tasks = [];
    }
    for (const managedTask of virtualWork.tasks) {
      this._tasks.push(managedTask);
    }
    if (!this.virtualRelevantForEdges && edges && virtualWork.tasks.length) {
      this.virtualRelevantForEdges = true;
    }
    if (!this.virtualRelevantForStartNode && startNode && virtualWork.tasks.length) {
      this.virtualRelevantForStartNode = true;
    }
    if (!this.virtualRelevantForEndNode && endNode && virtualWork.tasks.length) {
      this.virtualRelevantForEndNode = true;
    }
    if (!this.virtualRelevantForMidNodes && midNodes && virtualWork.tasks.length) {
      this.virtualRelevantForMidNodes = true;
    }
    if (virtualWork.tasks.find((_) => typeof _.sortOrder === 'number')) {
      this._tasks = this._tasks.sort((t1, t2) => {
        const o1 = t1.sortOrder ?? 100;
        const o2 = t2.sortOrder ?? 100;
        if (o1 < o2) {
          return -1;
        } else if (o1 > o2) {
          return 1;
        } else {
          return 0;
        }
      });
    }
  }

  constructor(private defaultHeaderTypeDisplayText: string) {}

  get allTasksFinished(): boolean {
    return this.tasks.filter((t) => t.isVisible).every((u) => u.finished);
  }

  get anyTaskFinished(): boolean {
    return this.tasks.filter((t) => t.isVisible).some((u) => u.finished);
  }

  get anyResultSelected(): boolean {
    return this.tasks.filter((t) => t.isVisible).some((u) => u.anyResultSelected);
  }

  refreshState() {
    let newState: number = WorkState.Beauftragt;
    if (this.allTasksFinished) {
      newState = WorkState.Erledigt;
    } else if (this.anyTaskFinished) {
      newState = WorkState.Teilerledigt;
    }

    if (this.state !== newState) {
      this.refreshExecutionDate();

      if (this.virtualGeneratedFromEntity) {
        this.persistenceService.addObjectValue(this, 'GeneratedFromObjectId', this.virtualGeneratedFromEntity.id);
        if (typeof this.virtualGeneratedFromEntity.objectType === 'number') {
          this.persistenceService.addObjectValue(
            this,
            'GeneratedFromObjecttype',
            this.virtualGeneratedFromEntity.objectType,
          );
        }
      }
      this.persistenceService.addObjectValue(this, 'State', newState, this.state);
    }
    this.state = newState;
  }

  private refreshExecutionDate() {
    const curDate = ConvertHelper.toISODateString(new Date());
    this.persistenceService.addObjectValue(this, 'ExecutionDate', curDate, this.executionDate);
    this.executionDate = curDate;
  }

  getValues(): string[] {
    const values: string[] = [];
    CollectionHelper.safePushValueToArray<string>(values, this.sortOrder?.toString());
    CollectionHelper.safePushValueToArray<string>(values, this.designation);
    CollectionHelper.safePushValueToArray<string>(values, this.objectDesignation);
    CollectionHelper.safePushValueToArray<string>(values, this.objectObjectType);
    CollectionHelper.safePushValueToArray<string>(values, this.street);
    CollectionHelper.safePushValueToArray<string>(values, this.note);
    CollectionHelper.safePushValueToArray<string>(values, this.executionDate);
    return values;
  }

  toString(): string {
    let ret: string = '';
    if (this.sortOrder) {
      ret = this.sortOrder + '.';
    }
    ret += ' ' + this.designation;
    if (this.objectObjectType) {
      ret += ', ' + this.objectObjectType;
    }
    if (this.objectDesignation) {
      ret += ', ' + this.objectDesignation;
    }
    if (this.street) {
      ret += ', ' + this.street;
    }
    if (this.note) {
      ret += ', ' + this.note;
    }
    if (this.isVirtual && this.managedWorks && this.managedWorks.length > 0) {
      ret += this.managedWorks.length === 1 ? ' (1 Arbeit)' : ` (${this.managedWorks.length} Arbeiten)`;
    }
    return ret;
  }

  public async calcWorkObjectsMainType(objectSpaceService$: ObjectSpaceService, force: boolean = false): Promise<void> {
    if (this.object) {
      await this.object.calcMainType(objectSpaceService$, force);
    }
    if (this.managedWorks) {
      for (const work of this.managedWorks) {
        await work.object?.calcMainType(objectSpaceService$, force);
      }
    }
  }

  //Not even used?
  public get refObjectsMainTypeObjectTypes(): ObjectTypeInfoViewModel[] {
    return this.getRefObjectsMainTypeProperties(
      (o) => o.mainTypeObjectType,
      (p) => p.name,
    );
  }

  public get refObjectsMainTypeDisplayTextsSingular(): string[] {
    return this.getRefObjectsMainTypeProperties(
      (o) => o.mainTypeDisplayTextSingular,
      (p) => p,
    );
  }

  public get refObjectsMainTypeDisplayTextsPlural(): string[] {
    return this.getRefObjectsMainTypeProperties(
      (o) => o.mainTypeDisplayTextPlural,
      (p) => p,
    );
  }

  private getRefObjectsMainTypeProperties<T>(
    getObjectProperty: (o: MobileObjectViewModel) => T,
    getPropComparable: (p: T) => string,
  ): T[] {
    return this.isVirtual
      ? CollectionHelper.removeDuplicates(
          (this.managedWorks ?? []).map((w) => getObjectProperty(w.object)),
          (ot1, ot2) => getPropComparable(ot1) === getPropComparable(ot2),
        ).sort((a, b) => getPropComparable(a).localeCompare(getPropComparable(b)))
      : this.object
        ? [getObjectProperty(this.object)]
        : [];
  }

  private getAggregatedModeCategoryText(defaultText?: string): string {
    const texts: string[] = [];
    if (this.virtualRelevantForStartNode && this.virtualRelevantForMidNodes && this.virtualRelevantForEndNode) {
      texts.push('Knoten');
    } else {
      if (this.virtualRelevantForStartNode) {
        texts.push('Anfangsknoten');
      }
      if (this.virtualRelevantForMidNodes) {
        texts.push('Zwischenknoten');
      }
      if (this.virtualRelevantForEndNode) {
        texts.push('Endknoten');
      }
    }
    if (this.virtualRelevantForEdges) {
      texts.push('Kanten');
    }
    if (texts.length === 0) {
      if (defaultText) {
        texts.push(defaultText);
      } else {
        texts.push(...this.refObjectsMainTypeDisplayTextsPlural);
      }
    }
    return texts.join(' & ');
  }

  public get aggregatedModeCategoryHeaderText(): string {
    return `Arbeiten an ${this.getAggregatedModeCategoryText()}:`;
  }

  revertChanges() {
    const pack = this.persistenceService?.getPendingObjectPackage(this);
    if (pack && !CompareHelper.isEmptyObject(pack.fieldValueHistories)) {
      pack.setOriginalFieldHistoryValue('State', (value) => (this.mtWork.State = value));
      pack.setOriginalFieldHistoryValue('Duration', (value) => (this.mtWork.Duration = value));
    }
  }

  remove() {
    this.persistenceService.addObjectForDelete(this);
  }
}

export class MobileTaskViewModel implements IPersistable, IDrawable {
  isVirtual: boolean;
  private _resultCategories: {};

  private persistenceService: PersistenceService;
  public mtTask: MtTask;
  private work: MobileWorkViewModel;
  private resultTypesGetter: (positionTypeId: string) => MtResultType[];
  private isResultTypeSelectedByDefault: (positionTypeId: string, resultTypeId: string) => boolean;
  private isResultTypeSelectedByAutoComplete: (positionTypeId: string, resultTypeId: string) => boolean;
  private getResultTypeSortOrder: (positionTypeId: string, resultTypeId: string) => number;
  private getResultTypeDesignation: (positionTypeId: string, resultTypeId: string) => string;
  private getResultTypeFollowingTaskTypeId: (positionTypeId: string, resultTypeId: string) => string;
  private getResultTypeInvisible: (positionTypeId: string, resultTypeId: string) => boolean;

  private virtualParent: MobileTaskViewModel;
  private managedTasks: MobileTaskViewModel[];
  private virtualDesignation: string;
  private virtualDescription: string;
  private virtualDescriptionOrig: string;
  private virtualSortOrder: number;
  private virtualAllowMultiSelectResults: boolean;
  private virtualAllowSelectResultsFromSingleCategory: boolean;
  private virtualResultsRequired: boolean;
  private virtualRelevantForEdges: boolean;
  private virtualRelevantForStartNode: boolean;
  private virtualRelevantForEndNode: boolean;
  private virtualRelevantForMidNodes: boolean;
  private virtualFinished: boolean;
  private virtualFinishedOrig: boolean;

  public allResults: MobileResultViewModel[];

  get id(): string {
    return this.mtTask?.Id;
  }
  get objectType(): string {
    return WorkConstants.ObjectTypeName_MtTask;
  }
  get persistenceCategory(): string {
    return WorkConstants.PersistenceCategory_WorkDetail;
  }
  get designation(): string {
    return this.isVirtual ? this.virtualDesignation : this.mtTask.Designation;
  }
  get description(): string {
    return this.isVirtual ? this.virtualDescription : this.mtTask.Description;
  }
  set description(value: string) {
    if (this.isVirtual) {
      for (const managedTask of this.managedTasks) {
        managedTask.description = value;
      }
      this.virtualDescription = value;
    } else if (this.mtTask.Description !== value) {
      this.persistenceService.addObjectValue(this, 'Description', value, this.mtTask.Description);
      this.mtTask.Description = value;
    }
  }
  get sortOrder(): number {
    return this.isVirtual ? this.virtualSortOrder : this.mtTask.SortOrder;
  }
  get finished(): boolean {
    return this.isVirtual ? this.virtualFinished : this.mtTask.Finished;
  }
  get anyResultSelected(): boolean {
    return this.allResults?.some((r) => r.selected);
  }
  get allowMultiSelectResults(): boolean {
    return this.isVirtual ? this.virtualAllowMultiSelectResults : this.mtTask.AllowMultiSelectResults;
  }
  get allowSelectResultsFromSingleCategory(): boolean {
    return this.isVirtual
      ? this.virtualAllowSelectResultsFromSingleCategory
      : this.mtTask.AllowSelectResultsFromSingleCategory;
  }
  get resultsRequired(): boolean {
    return this.isVirtual ? this.virtualResultsRequired : this.mtTask.ResultsRequired;
  }
  get relevantForEdges(): boolean {
    return this.isVirtual ? this.virtualRelevantForEdges : (this.mtTask.RelevantForEdges ?? false);
  }
  get relevantForStartNode(): boolean {
    return this.isVirtual ? this.virtualRelevantForStartNode : (this.mtTask.RelevantForStartNode ?? false);
  }
  get relevantForEndNode(): boolean {
    return this.isVirtual ? this.virtualRelevantForEndNode : (this.mtTask.RelevantForEndNode ?? false);
  }
  get relevantForMidNodes(): boolean {
    return this.isVirtual ? this.virtualRelevantForMidNodes : (this.mtTask.RelevantForMidNodes ?? false);
  }
  get preventFinishing(): boolean {
    this.ensureResultsAreLoaded();
    return this.resultsRequired && (!this.allResults || !this.allResults.some((f) => f.selected));
  }
  get allowPointGeometries(): boolean {
    return this.mtTask?.AllowPointGeometries ?? false;
  }
  get allowLineGeometries(): boolean {
    return this.mtTask?.AllowLineGeometries ?? false;
  }
  get allowPolygonGeometries(): boolean {
    return this.mtTask?.AllowPolygonGeometries ?? false;
  }
  get allowAnyGeometries(): boolean {
    return this.allowPointGeometries || this.allowLineGeometries || this.allowPolygonGeometries;
  }

  set finished(value: boolean) {
    const cancel = value && this.preventFinishing;
    if (!cancel) {
      if (this.isVirtual) {
        for (const managedTask of this.managedTasks) {
          managedTask.finished = value;
          managedTask.work.refreshState();
        }
        this.virtualFinished = value;
      } else if (this.mtTask.Finished !== value) {
        this.persistenceService.addObjectValue(this, 'Finished', value, this.mtTask.Finished);
        this.mtTask.Finished = value;
        this.work.refreshState();
      }
    }
  }

  get geometry(): any {
    return this.mtTask?.Geometry;
  }

  set geometry(value: any) {
    const cancel = value && this.preventFinishing;
    if (!cancel) {
      this.addOverwriteTag(value);
      this.persistenceService.addObjectValue(this, 'Geometry__Json', value, this.mtTask.Geometry);
      this.mtTask.Geometry = value;
      this.work.refreshState();
    }
  }

  get resultCategories(): {} {
    this.ensureResultsAreLoaded();
    return this._resultCategories;
  }

  get drawableObjectId(): string {
    return this.work.drawableObjectId;
  }
  get drawableObjectType(): number {
    return this.work.drawableObjectType;
  }

  private addOverwriteTag(obj: any) {
    if (obj) {
      obj.Tag = { Overwrite: true };
    }
  }

  private precedingResults: MobileResultViewModel[] = undefined;
  get isVisible(): boolean {
    if (this.isVirtual && this.managedTasks?.length > 0) {
      return this.managedTasks.some((t) => t.isVisible);
    }

    this.ensureResultsAreLoaded();
    if (this.precedingResults === undefined) {
      this.precedingResults = [];
      const taskTypeId = this.mtTask?.TaskTypeId;
      if (taskTypeId) {
        for (const t of this.work.tasks) {
          t.ensureResultsAreLoaded();
          for (const r of t.allResults) {
            if (
              taskTypeId === r.mtResultType?.IdRefMtTaskTypeFollowing ||
              taskTypeId === this.getResultTypeFollowingTaskTypeId(t.mtTask?.PositionTypeId, r.mtResultType?.Id)
            ) {
              this.precedingResults.push(r);
            }
          }
        }
      }
    }

    if (this.precedingResults?.length) {
      for (const r of this.precedingResults) {
        if (r.selected) {
          return true;
        }
      }
      return false;
    } else {
      return true;
    }
  }

  public ensureResultsAreLoaded() {
    if (!this._resultCategories) {
      this._resultCategories = {};
      this.allResults = new Array<MobileResultViewModel>();
      if (this.isVirtual) {
        for (const managedTask of this.managedTasks) {
          for (const strCategory in managedTask.resultCategories) {
            const results: MobileResultViewModel[] = this.ensureResultCategory(strCategory);
            results.push(...managedTask.resultCategories[strCategory || '']);
          }
        }

        for (const strCategory in this._resultCategories) {
          const groupedResults: Map<string, MobileResultViewModel[]> =
            ViewModelHelper.groupArray<MobileResultViewModel>(this._resultCategories[strCategory], 'designation');

          this._resultCategories[strCategory] = new Array<MobileResultViewModel>();

          groupedResults.forEach((v, k) => {
            const virtualResult = new MobileResultViewModel();
            const firstRealResult: MobileResultViewModel = v[0];
            const uniqueSelected = v.every((t) => t.selected === firstRealResult.selected)
              ? firstRealResult.selected
              : false;
            const uniqueDescription = v.every(
              (t) => t.description?.toLowerCase() === firstRealResult.description?.toLowerCase(),
            )
              ? firstRealResult.description
              : null;
            const uniqueQuantity = v.every((t) => t.quantity === firstRealResult.quantity)
              ? firstRealResult.quantity
              : null;
            const uniqueDate: Date = v.every((t) => t.date === firstRealResult.date) ? firstRealResult.date : null;
            const uniqueTime: Date = v.every((t) => t.time === firstRealResult.time) ? firstRealResult.time : null;
            virtualResult.loadVirtual(
              uniqueSelected,
              firstRealResult.designation,
              strCategory,
              uniqueDescription,
              v,
              this,
              uniqueQuantity,
              uniqueDate,
              uniqueTime,
            );
            if (virtualResult.isVisible) {
              this._resultCategories[strCategory].push(virtualResult);
              this.allResults.push(virtualResult);
            }
          });
        }
      } else {
        const resultTypes: MtResultType[] = this.resultTypesGetter(this.mtTask.PositionTypeId);
        for (const resultType of resultTypes) {
          const result: MobileResultViewModel = new MobileResultViewModel();
          result.load(
            this.persistenceService,
            resultType,
            this.mtTask.Results.find((r) => r.ResultTypeId === resultType.Id),
            this,
          );
          if (result.isVisible) {
            const results: MobileResultViewModel[] = this.ensureResultCategory(resultType.Category);
            results.push(result);
            this.allResults.push(result);
          }
        }
      }

      //sort asc
      for (const catKey in this._resultCategories) {
        this._resultCategories[catKey] = (this._resultCategories[catKey] as Array<MobileResultViewModel>).sort((a, b) =>
          a.designation.localeCompare(b.designation),
        );
      }
    }
  }

  public ensureDefaultValues() {
    if (!this.allResults.find((_) => _.isPersisted || _.selected)) {
      for (const result of this.allResults) {
        if (this.isSelectedByDefault(result.mtResultType)) {
          result.selected = true;
        }
      }
    }
  }

  private ensureResultCategory(strCategory: string) {
    const category = strCategory || '';
    if (!this._resultCategories[category]) {
      this._resultCategories[category] = new Array<MobileResultViewModel>();
    }
    return this._resultCategories[category];
  }

  load(
    persistenceService: PersistenceService,
    mtTask: MtTask,
    work: MobileWorkViewModel,
    resultTypesGetter: (positionTypeId: string) => MtResultType[],
    isResultTypeSelectedByDefault: (positionTypeId: string, resultTypeId: string) => boolean,
    isResultTypeSelectedByAutoComplete: (positionTypeId: string, resultTypeId: string) => boolean,
    getResultTypeSortOrder: (positionTypeId: string, resultTypeId: string) => number,
    getResultTypeDesignation: (positionTypeId: string, resultTypeId: string) => string,
    getResultTypeFollowingTaskTypeId: (positionTypeId: string, resultTypeId: string) => string,
    getResultTypeInvisible: (positionTypeId: string, resultTypeId: string) => boolean,
  ): void {
    this.persistenceService = persistenceService;
    this.mtTask = mtTask;
    this.work = work;
    this.resultTypesGetter = resultTypesGetter;
    this.isResultTypeSelectedByDefault = isResultTypeSelectedByDefault;
    this.isResultTypeSelectedByAutoComplete = isResultTypeSelectedByAutoComplete;
    this.getResultTypeSortOrder = getResultTypeSortOrder;
    this.getResultTypeDesignation = getResultTypeDesignation;
    this.getResultTypeFollowingTaskTypeId = getResultTypeFollowingTaskTypeId;
    this.getResultTypeInvisible = getResultTypeInvisible;
  }

  loadVirtual(
    designation: string,
    description: string,
    sortOrder: number,
    allowMultiSelectResults: boolean,
    allowSelectResultsFromSingleCategory: boolean,
    resultsRequired: boolean,
    relevantForEdges: boolean,
    relevantForStartNode: boolean,
    relevantForEndNode: boolean,
    relevantForMidNodes: boolean,
    finished: boolean,
    managedTasks: MobileTaskViewModel[],
  ): void {
    this.isVirtual = true;
    this.virtualDesignation = designation;
    this.virtualDescription = description;
    this.virtualDescriptionOrig = description;
    this.virtualSortOrder = sortOrder;
    this.virtualAllowMultiSelectResults = allowMultiSelectResults;
    this.virtualAllowSelectResultsFromSingleCategory = allowSelectResultsFromSingleCategory;
    this.virtualResultsRequired = resultsRequired;
    this.virtualRelevantForEdges = relevantForEdges;
    this.virtualRelevantForStartNode = relevantForStartNode;
    this.virtualRelevantForEndNode = relevantForEndNode;
    this.virtualRelevantForMidNodes = relevantForMidNodes;
    this.virtualFinished = finished;
    this.virtualFinishedOrig = finished;
    this.managedTasks = managedTasks;

    for (const managedTask of this.managedTasks) {
      managedTask.virtualParent = this;
      this.isResultTypeSelectedByDefault = managedTask.isResultTypeSelectedByDefault;
      this.isResultTypeSelectedByAutoComplete = managedTask.isResultTypeSelectedByAutoComplete;
      this.getResultTypeSortOrder = managedTask.getResultTypeSortOrder;
      this.getResultTypeDesignation = managedTask.getResultTypeDesignation;
      this.getResultTypeFollowingTaskTypeId = managedTask.getResultTypeFollowingTaskTypeId;
      this.getResultTypeInvisible = managedTask.getResultTypeInvisible;
    }
  }

  constructor() {}

  changesPersisted(persistMode: PersistMode) {
    if (this.isVirtual) {
      if (
        persistMode === PersistMode.Insert ||
        persistMode === PersistMode.Update ||
        persistMode === PersistMode.Merge
      ) {
        this.virtualDescriptionOrig = this.virtualDescription;
        this.virtualFinishedOrig = this.virtualFinished;
      }
    }
    if (this.virtualParent) {
      this.virtualParent.changesPersisted(persistMode);
    }
  }
  revertChanges() {
    if (this.isVirtual) {
      this.virtualDescription = this.virtualDescriptionOrig;
      this.virtualFinished = this.virtualFinishedOrig;
    } else {
      const pack = this.persistenceService.getPendingObjectPackage(this);
      if (pack && !CompareHelper.isEmptyObject(pack.fieldValueHistories)) {
        pack.setOriginalFieldHistoryValue('Finished', (value) => (this.mtTask.Finished = value));
        pack.setOriginalFieldHistoryValue('Description', (value) => (this.mtTask.Description = value));
      }
      if (this.virtualParent) {
        this.virtualParent.revertChanges();
      }
    }
  }

  public isSelectedByDefault(mtResultType: MtResultType): boolean {
    if (this.managedTasks?.length) {
      return this.managedTasks[0].isSelectedByDefault(mtResultType);
    }
    return (
      this.isResultTypeSelectedByDefault &&
      this.isResultTypeSelectedByDefault(this.mtTask?.PositionTypeId, mtResultType?.Id)
    );
  }

  public isSelectedByAutoComplete(mtResultType: MtResultType): boolean {
    if (this.managedTasks?.length) {
      return this.managedTasks[0].isSelectedByAutoComplete(mtResultType);
    }
    return (
      this.isResultTypeSelectedByAutoComplete &&
      this.isResultTypeSelectedByAutoComplete(this.mtTask?.PositionTypeId, mtResultType?.Id)
    );
  }

  public getSortOrder(mtResultType: MtResultType): number {
    if (this.managedTasks?.length) {
      return this.managedTasks[0].getSortOrder(mtResultType);
    }
    return this.getResultTypeSortOrder && this.getResultTypeSortOrder(this.mtTask?.PositionTypeId, mtResultType?.Id);
  }

  public getDesignation(mtResultType: MtResultType): string {
    if (this.managedTasks?.length) {
      return this.managedTasks[0].getDesignation(mtResultType);
    }
    return (
      this.getResultTypeDesignation && this.getResultTypeDesignation(this.mtTask?.PositionTypeId, mtResultType?.Id)
    );
  }

  public getInvisible(mtResultType: MtResultType): boolean {
    if (this.managedTasks?.length) {
      return this.managedTasks[0].getInvisible(mtResultType);
    }
    return this.getResultTypeInvisible && this.getResultTypeInvisible(this.mtTask?.PositionTypeId, mtResultType?.Id);
  }

  public toString(): string {
    return this.work.toString() + ' [' + this.designation + ']';
  }
}

export class MobileResultViewModel implements IPersistable, IDrawable {
  private _hasPersistedDataObject: boolean;
  private _selected: boolean;
  private _designation: string;
  private _description: string;
  private _photoImpossibleText: string;
  private _quantity?: number;
  private _category: string;
  private _code: string;
  private _caption: string;
  private _unit: string;
  private _unitPrice: number;
  private _date: Date;
  private _dateCache: Date;
  private _time: Date;
  private _timeCache: Date;
  private _id: string;
  private _documents: MobileDocumentViewModel[];

  private persistenceService: PersistenceService;
  public mtResultType: MtResultType;
  private mtResult: MtResult;
  private task: MobileTaskViewModel;

  private isVirtual: boolean;
  private virtualParent: MobileResultViewModel;
  private managedResults: MobileResultViewModel[];
  private virtualSelected: boolean;
  private virtualSelectedOrig: boolean;
  private virtualDesignation: string;
  private virtualCategory: string;
  private virtualDescription: string;
  private virtualPhotoImpossibleText: string;
  private virtualDescriptionOrig: string;
  private virtualQuantity?: number;
  private virtualQuantityOrig?: number;
  private virtualDate: Date;
  private virtualDateOrig: Date;
  private virtualTime: Date;
  private virtualTimeOrig: Date;

  get id(): string {
    return this._id;
  }
  get objectType(): string {
    return WorkConstants.ObjectTypeName_MtResult;
  }
  get persistenceCategory(): string {
    return WorkConstants.PersistenceCategory_TaskDetail;
  }
  get isPersisted(): boolean {
    return this._hasPersistedDataObject || this.mtResult ? true : false;
  }
  get selected(): boolean {
    return this.isVirtual ? this.virtualSelected : this._selected;
  }
  set selected(value: boolean) {
    if (this.isVirtual) {
      this.virtualSelected = value;
      for (const managedResult of this.managedResults) {
        managedResult.selected = value;
        if (value && managedResult.mtResultType?.SetTaskCompleteOnSelection) {
          this.task.finished = true;
        }
      }
    } else if (this._selected !== value) {
      this._selected = value;
      if (value) {
        if (!this.mtResult) {
          this.mtResult = new MtResult({
            Id: this.id,
            Designation: this._designation,
            Description: this._description,
            PhotoImpossibleText: this._photoImpossibleText,
            Quantity: this._quantity,
            Category: this._category,
            Caption: this._caption,
            Unit: this._unit,
            UnitPrice: this._unitPrice,
            Price: this.price,
            Code: this._code,
            Date: this._date,
            Time: this._time,
            ResultTypeId: this.mtResultType?.Id,
          });
        }
        if (this.task.mtTask.Results.findIndex((_) => _ == this.mtResult) < 0) {
          this.task.mtTask.Results.push(this.mtResult);
        }
        if (!this._hasPersistedDataObject) {
          //fill date/time field defaults
          this._date = new Date();
          this._time = this._date;
          this.mtResult.Date = ConvertHelper.toISODateString(this._date);
          this.mtResult.Time = ConvertHelper.toISOTimeString(this._time);

          this.persistenceService.removeObject(this); //remove possible duplicate
          this.persistenceService.addObjectForMerge(this);
          this.persistenceService.addObjectValue(this, 'ParentId', this.task.id);
          this.persistenceService.addObjectValue(this, 'ResultTypeId', this.mtResultType.Id);
          this.persistenceService.addObjectValue(this, 'Designation', this._designation);
          this.persistenceService.addObjectValue(this, 'Description', this._description);
          this.persistenceService.addObjectValue(this, 'Quantity', this._quantity);
          this.persistenceService.addObjectValue(this, 'Category', this._category);
          this.persistenceService.addObjectValue(this, 'Code', this._code);
          this.persistenceService.addObjectValue(this, 'Caption', this._caption);
          this.persistenceService.addObjectValue(this, 'Unit', this._unit);
          this.persistenceService.addObjectValue(this, 'UnitPrice', this._unitPrice);
          this.persistenceService.addObjectValue(this, 'Price', this.price);
          this.persistenceService.addObjectValue(this, 'Date', this.mtResult.Date);
          this.persistenceService.addObjectValue(this, 'Time', this.mtResult.Time);
          this.persistenceService.addObjectValue(
            this,
            DataService.COMMENT_NO_PHOTO_IS_POSSIBLE_OPATH,
            this.mtResult.PhotoImpossibleText,
          );

          if (this.mtResultType.SetTaskCompleteOnSelection) {
            this.task.finished = true;
          }

          //this.persistTakingPhotoPossibilityAsComment();
        } else {
          this.persistenceService.removeObject(this); //remove deletion (addObjectForDelete()-call below/before)
        }
      } else {
        if (this.id) {
          this.persistenceService.addObjectForDelete(this);
        }
        this.task.mtTask.Results = this.task.mtTask.Results.filter((_) => _ !== this.mtResult);
        if (!this._hasPersistedDataObject) {
          this.mtResult = null;
          this._date = null;
          this._time = null;
          this._dateCache = null;
          this._timeCache = null;
        }

        if (this.task.resultsRequired && !this.task.allResults.filter((f) => f !== this).some((r) => r.selected)) {
          this.task.finished = false;
        }
      }
    }

    if (value) {
      if (this.task.allowSelectResultsFromSingleCategory) {
        for (const strResultCategory in this.task.resultCategories) {
          if (strResultCategory !== this.mtResultType.Category) {
            this.task.resultCategories[strResultCategory || '']
              .filter((f) => f.selected)
              .forEach((r) => (r.selected = false));
          }
        }
      }
      if (!this.task.allowMultiSelectResults) {
        //prohibit MultiSelectResults covering ALL categories (only one result at all)
        //this.task.allResults.filter(f => f !== this && f.selected).forEach(r => r.selected = false);

        //prohibit MultiSelectResults PER category
        this.task.resultCategories[this.mtResultType.Category || '']
          .filter((f) => f !== this && f.selected)
          .forEach((r) => (r.selected = false));
      }
    }
  }
  get taskAllowPointGeometries(): boolean {
    return this.task?.allowPointGeometries;
  }
  get taskAllowLineGeometries(): boolean {
    return this.task?.allowLineGeometries;
  }
  get taskAllowPolygonGeometries(): boolean {
    return this.task?.allowPolygonGeometries;
  }
  get taskAllowAnyGeometries(): boolean {
    return this.task?.allowAnyGeometries;
  }

  get documents(): MobileDocumentViewModel[] {
    if (!this._documents) {
      this._documents = [];
      if (this.isVirtual) {
        for (const managedResult of this.managedResults) {
          if (managedResult.documents) {
            for (const doc of managedResult.documents) {
              if (!this._documents.find((_) => _.designation === doc.designation)) {
                this._documents.push(doc);
              }
            }
          }
        }
      } else if (this._hasPersistedDataObject) {
        for (const document of this.mtResult.Documents) {
          const d: MobileDocumentViewModel = new MobileDocumentViewModel();
          d.load(this.persistenceService, document, this.persistenceCategory, true);
          this._documents.push(d);
        }
      }
      MTHelper.sortMobileDocuments(this._documents);
    }
    return this._documents;
  }

  public addDocumentByDataUrl(designation: string, date: Date, dataUrl: DataUrl): MobileDocumentViewModel {
    const newDocVM: MobileDocumentViewModel = new MobileDocumentViewModel();
    const newMtDoc: MtDocument = newDocVM.createNewByDataUrl(
      this.persistenceService,
      this.persistenceCategory,
      this,
      designation,
      date,
      dataUrl,
    );
    this.mtResult.Documents.push(newMtDoc);
    this.documents.push(newDocVM);
    return newDocVM;
  }

  public addDocumentByFile(designation: string, date: Date, file: File): MobileDocumentViewModel {
    const newDocVM: MobileDocumentViewModel = new MobileDocumentViewModel();
    const newMtDoc: MtDocument = newDocVM.createNewByFile(
      this.persistenceService,
      this.persistenceCategory,
      this,
      designation,
      date,
      file,
    );
    this.mtResult.Documents.push(newMtDoc);
    this.documents.push(newDocVM);
    return newDocVM;
  }

  public revertDocuments(): void {
    if (this._documents) {
      const nonPersistedDocs = this._documents.filter((d) => !d.isPersisted);
      this._documents = this._documents.filter((d) => nonPersistedDocs.indexOf(d) === -1);

      if (this._hasPersistedDataObject) {
        this.mtResult.Documents = this.mtResult.Documents.filter(
          (d) => nonPersistedDocs.map((d2) => d2.mtDocument).indexOf(d) === -1,
        );
      }
    }
  }

  get photoImpossibleText(): string {
    return this.isVirtual ? this.virtualPhotoImpossibleText : this.mtResult?.PhotoImpossibleText;
  }
  get designation(): string {
    return this.isVirtual ? this.virtualDesignation : this.mtResult ? this.mtResult.Designation : this._designation;
  }
  get category(): string {
    return this.isVirtual ? this.virtualCategory : this.mtResult ? this.mtResult.Category : this._category;
  }
  get description(): string {
    return this.isVirtual ? this.virtualDescription : this.mtResult ? this.mtResult.Description : this._description;
  }
  set description(value: string) {
    if (this.isVirtual) {
      for (const managedResult of this.managedResults) {
        managedResult.description = value;
      }
      this.virtualDescription = value;
    } else if (this.mtResult.Description !== value) {
      if (this.mtResult) {
        this.persistenceService.addObjectValue(this, 'Description', value, this.mtResult.Description);
        this.mtResult.Description = value;
      } else {
        this._description = value;
      }
    }
  }

  get drawableObjectId(): string {
    return this.task.drawableObjectId;
  }
  get drawableObjectType(): number {
    return this.task.drawableObjectType;
  }

  get quantity(): number {
    return this.isVirtual ? this.virtualQuantity : this.mtResult ? this.mtResult.Quantity : this._quantity;
  }
  set quantity(value: number) {
    if (this.isVirtual) {
      for (const managedResult of this.managedResults) {
        managedResult.quantity = value;
      }
      this.virtualQuantity = value;
    } else {
      if (this.mtResult) {
        if (this.mtResult.Quantity !== value) {
          this.persistenceService.addObjectValue(this, 'Quantity', value, this.mtResult.Quantity);
          this.mtResult.Quantity = value;
        }
        if (this.mtResult.Caption !== this._caption) {
          this.persistenceService.addObjectValue(this, 'Caption', this._caption, this.mtResult.Caption);
          this.mtResult.Caption = this._caption;
        }
        if (this.mtResult.Unit !== this._unit) {
          this.persistenceService.addObjectValue(this, 'Unit', this._unit, this.mtResult.Unit);
          this.mtResult.Unit = this._unit;
        }
        if (this.mtResult.UnitPrice !== this._unitPrice) {
          this.persistenceService.addObjectValue(this, 'UnitPrice', this._unitPrice, this.mtResult.UnitPrice);
          this.mtResult.UnitPrice = this._unitPrice;
        }
        if (this.mtResult.Price !== this.price) {
          this.persistenceService.addObjectValue(this, 'Price', this.price, this.mtResult.Price);
          this.mtResult.Price = this.price;
        }
      } else {
        this._quantity = value;
      }
    }
  }

  get date(): Date {
    if (!this._dateCache) {
      if (this.mtResult && this.mtResult.Date) {
        this._dateCache = new Date(this.mtResult.Date);
      } else {
        this._dateCache = this._date;
      }
    }
    let result = this.isVirtual ? this.virtualDate : this.mtResult ? this._dateCache : this._date;
    if (!result && this.isVirtual) {
      for (const managedResult of this.managedResults) {
        if (managedResult.date) {
          result = managedResult.date;
          break;
        }
      }
    }
    return result;
  }
  set date(value: Date) {
    if (this.isVirtual) {
      for (const managedResult of this.managedResults) {
        managedResult.date = value;
      }
      this.virtualDate = value;
    } else {
      if (this.mtResult) {
        const d: string = ConvertHelper.toISODateString(value);
        if (this.mtResult.Date !== d) {
          this.persistenceService.addObjectValue(this, 'Date', d, this.mtResult.Date);
          this.mtResult.Date = d;
        }
        this._dateCache = value;
      } else {
        this._date = value;
      }
    }
  }

  get time(): Date {
    if (!this._timeCache) {
      if (this.mtResult && this.mtResult.Time) {
        this._timeCache = ViewModelHelper.parseDateTime(this._date, this.mtResult.Time);
      } else {
        this._timeCache = this._time;
      }
    }
    let result = this.isVirtual ? this.virtualTime : this.mtResult ? this._timeCache : this._time;
    if (!result && this.isVirtual) {
      for (const managedResult of this.managedResults) {
        if (managedResult.time) {
          result = managedResult.time;
          break;
        }
      }
    }
    return result;
  }

  set time(value: Date) {
    if (this.isVirtual) {
      for (const managedResult of this.managedResults) {
        managedResult.time = value;
      }
      this.virtualTime = value;
    } else {
      if (this.mtResult) {
        const t: string = value ? ViewModelHelper.ensureValidIsoString(this.mtResult.Time, value) : null;
        if (this.mtResult.Time !== t) {
          this.persistenceService.addObjectValue(this, 'Time', t, this.mtResult.Time);
          this.mtResult.Time = t;
        }
        this._timeCache = value;
      } else {
        this._time = value;
      }
    }
  }

  get caption(): string {
    return this.mtResultType.Caption;
  }

  get unit(): string {
    return this.mtResultType.Unit;
  }

  get price(): number {
    return this.mtResult ? this.mtResult.Quantity * this.mtResult.UnitPrice : this._unitPrice * this._quantity;
  }

  get photoImpossible(): boolean {
    const text: string = this.isVirtual
      ? this.virtualPhotoImpossibleText
      : this.mtResult
        ? this.mtResult.PhotoImpossibleText
        : this._photoImpossibleText;

    return text ? true : false;
  }

  set photoImpossible(value: boolean) {
    const newValueText = value ? DataService.COMMENT_NO_PHOTO_IS_POSSIBLE : null;
    if (this.isVirtual) {
      for (const managedResult of this.managedResults) {
        managedResult.photoImpossible = value;
      }
      this.virtualPhotoImpossibleText = newValueText;
    } else {
      if (this.mtResult) {
        if (this.mtResult.PhotoImpossibleText !== newValueText) {
          this.persistenceService.addObjectValue(
            this,
            DataService.COMMENT_NO_PHOTO_IS_POSSIBLE_OPATH,
            newValueText,
            this.mtResult.PhotoImpossibleText,
          );
          this.mtResult.PhotoImpossibleText = newValueText;
        }
      } else {
        this._photoImpossibleText = newValueText;
      }
    }
    if (this.photoImpossible !== value) {
      if (this.mtResult) {
        this.mtResult.PhotoImpossibleText = newValueText;
      } else {
        this._photoImpossibleText = newValueText;
      }
    }
  }

  get isVisible(): boolean {
    if (this.isVirtual) {
      for (const managedResult of this.managedResults) {
        if (managedResult.task.getInvisible(managedResult.mtResultType) !== true) {
          return true;
        }
      }
      return false;
    } else if (this.task.getInvisible(this.mtResultType) === true) {
      return false;
    } else {
      return true;
    }
  }

  load(
    persistenceService: PersistenceService,
    mtResultType: MtResultType,
    mtResult: MtResult,
    task: MobileTaskViewModel,
  ) {
    this.persistenceService = persistenceService;
    this.mtResultType = mtResultType;
    this.mtResult = mtResult;
    this.task = task;

    this._id = mtResult?.Id ?? GUID.newGUID();
    this._hasPersistedDataObject = !CompareHelper.isEmptyObject(mtResult);
    this._selected = this._hasPersistedDataObject;

    this.fillDefaultValues();
  }

  loadVirtual(
    selected: boolean,
    designation: string,
    category: string,
    description: string,
    managedResults: MobileResultViewModel[],
    task: MobileTaskViewModel,
    quantity?: number,
    date?: Date,
    time?: Date,
  ): void {
    this.isVirtual = true;
    this.virtualSelected = selected;
    this.virtualSelectedOrig = selected;
    this.virtualDesignation = designation;
    this.virtualCategory = category;
    this.virtualDescription = description;
    this.virtualDescriptionOrig = description;
    this.virtualQuantity = quantity;
    this.virtualQuantityOrig = quantity;
    this.virtualDate = date;
    this.virtualDateOrig = date;
    this.virtualTime = time;
    this.virtualTimeOrig = time;
    this.task = task;
    this.managedResults = managedResults;
    this.mtResultType = this.managedResults[0].mtResultType; //HACK

    for (const managedResult of this.managedResults) {
      managedResult.virtualParent = this;
    }
  }

  constructor() {}

  changesPersisted(persistMode: PersistMode) {
    if (this.isVirtual) {
      this.virtualSelectedOrig = this.virtualSelected;
      this.virtualDescriptionOrig = this.virtualDescription;
      this.virtualQuantityOrig = this.virtualQuantity;
      this.virtualDateOrig = this.virtualDate;
      this.virtualTimeOrig = this.virtualTime;
    } else {
      if (
        persistMode === PersistMode.Insert ||
        persistMode === PersistMode.Update ||
        persistMode === PersistMode.Merge
      ) {
        this._hasPersistedDataObject = true;
      } else {
        this._hasPersistedDataObject = false;
        this.mtResult = null;
        this.fillDefaultValues();
      }
      if (this.virtualParent) {
        this.virtualParent.changesPersisted(persistMode);
      }
    }
  }
  revertChanges() {
    if (this.isVirtual) {
      this.virtualSelected = this.virtualSelectedOrig;
      this.virtualDescription = this.virtualDescriptionOrig;
      this.virtualQuantity = this.virtualQuantityOrig;
      this.virtualDate = this.virtualDateOrig;
      this.virtualTime = this.virtualTimeOrig;
    } else {
      if (this._hasPersistedDataObject) {
        const pack = this.persistenceService.getPendingObjectPackage(this);
        if (pack && !CompareHelper.isEmptyObject(pack.fieldValueHistories)) {
          pack.setOriginalFieldHistoryValue('Designation', (value) => (this.mtResult.Designation = value));
          pack.setOriginalFieldHistoryValue('Description', (value) => (this.mtResult.Description = value));
          pack.setOriginalFieldHistoryValue('Quantity', (value) => (this.mtResult.Quantity = value));
          pack.setOriginalFieldHistoryValue('Unit', (value) => (this.mtResult.Unit = value));
          pack.setOriginalFieldHistoryValue('UnitPrice', (value) => (this.mtResult.UnitPrice = value));
          pack.setOriginalFieldHistoryValue('Price', (value) => (this.mtResult.Price = value));
          pack.setOriginalFieldHistoryValue('Date', (value) => (this.mtResult.Date = value));
          pack.setOriginalFieldHistoryValue('Time', (value) => (this.mtResult.Time = value));
          pack.setOriginalFieldHistoryValue(
            DataService.COMMENT_NO_PHOTO_IS_POSSIBLE_OPATH,
            (value) => (this.mtResult.PhotoImpossibleText = value),
          );
        }
        this.revertDocuments();

        this._selected = true;
        this._dateCache = null;
        this._timeCache = null;
      } else {
        this.mtResult = null;
        this._selected = false;
        this.fillDefaultValues();
      }
      if (this.virtualParent) {
        this.virtualParent.revertChanges();
      }
    }
  }

  fillDefaultValues() {
    this._designation = this.mtResultType.Designation;
    this._description = this.mtResultType.Description;
    this._quantity = this.mtResultType.Quantity;
    this._category = this.mtResultType.Category;
    this._code = this.mtResultType.Code;
    this._caption = this.mtResultType.Caption;
    this._unit = this.mtResultType.Unit;
    this._unitPrice = this.mtResultType.UnitPrice;
    this._date = null;
    this._dateCache = null;
    this._time = null;
    this._timeCache = null;
    this._photoImpossibleText = null;
  }

  public getAllManagedResults(): MobileResultViewModel[] {
    if (this.isVirtual) {
      return this.managedResults;
    } else {
      return [this];
    }
  }
}

export class MobileOrderViewModel {
  private _date: Date;

  get id(): string {
    return this.order.Id;
  }
  get designation(): string {
    return this.order.Designation;
  }
  get remark(): string {
    return this.order.Remark;
  }
  get date(): Date {
    return this._date;
  }
  get countWorks(): number {
    return this.order.CountWorks;
  }
  get countWorksOpen(): number {
    return this.order.CountWorksOpen;
  }
  get countWorksPartial(): number {
    return this.order.CountWorksPartial;
  }
  get countWorksDone(): number {
    return this.order.CountWorksDone;
  }
  get dbObject(): Order {
    return this.order;
  }
  get donePercentage(): number {
    return (this.countWorksDone / this.countWorks) * 100;
  }

  constructor(
    private order: Order,
    public readonly isDownloaded: boolean,
  ) {
    this._date = new Date(this.order.Date);
  }

  public get canSelect(): boolean {
    return this.isDownloaded || !CacheStorageAdapter.useOfflineMode || !CacheStorageAdapter.isDownloadComplete;
  }
}
